import { useCallback, useMemo, useState } from "react";
import {
  Theme,
  alpha,
  decomposeColor,
  recomposeColor,
} from "@mui/material/styles";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "react-query";
import { useRecoilValue } from "recoil";

import AppButton from "../components/AppButton";
import ResponsiveDialog from "../components/ResponsiveDialog";
import Field, { FIELD_INPUT } from "../components/Field";
import Section from "../components/Section";
import { CustomAutocomplete } from "../components/Inputs/CustomAutocomplete";
import ExclusiveToggleGroup from "../components/Inputs/ExclusiveToggleGroup";
import StyledDataGrid from "../components/Table/StyledDataGrid";
import { GRID_MAX_HEIGHT } from "../components/Table/styles";
import { CALENDAR_ROW_HEIGHT, HEADER_HEIGHT } from "../constants";
import { SCROLL_BAR_WIDTH } from "../Theme";
import { formInputsStyles } from "../Configuration/styles";
import { FormatterService, formatterService } from "../NumberFormatter";
import FillRuleDialog from "./FillRuleDialog";
import { fetchGeneratedLevels, fetchProperties } from "./api";
import {
  applyFillRule,
  cellError,
  gridToClipboard,
  hasAnyPrice,
  newLevel,
  parseClipboard,
  pasteInto,
  proposalIsEmpty,
  stepsOf,
  stripHeaders,
} from "./grid";
import {
  FillRule,
  GridDraft,
  PropertyOption,
  ProposedGrid,
  RoundingDirection,
  UhTypeFollow,
} from "./types";

// The same three words as the generator's rounding ("au plus proche",
// "au-dessus", "en dessous"), so they share the keys rather than duplicate them.
const SNAP_OPTIONS: { value: RoundingDirection; label: string }[] = [
  { value: "nearest", label: "PRICING_TIER_ROUNDING_NEAREST" },
  { value: "up", label: "PRICING_TIER_ROUNDING_UP" },
  { value: "down", label: "PRICING_TIER_ROUNDING_DOWN" },
];

// How strongly a cell is tinted, from the cheapest price in the grid to the
// dearest. The floor is not zero: a cell at the very bottom still has to read
// as a priced cell rather than an empty one. The ceiling stays low — the
// digits have to keep their contrast against it.
const TINT_MIN = 0.03;
const TINT_MAX = 0.16;

const PRODUCT_COLUMN_WIDTH = 240;
// Wide enough for a level name of about fifteen characters on its own line.
const LEVEL_COLUMN_WIDTH = 92;
const ADD_COLUMN_WIDTH = 52;
// A product cell carries two lines — its name and either its room type or its
// rule — so the calendar's taller row is the right house measure here.
const GRID_ROW_HEIGHT = CALENDAR_ROW_HEIGHT;
// The level header carries two lines too: its rank, then its name.
const GRID_HEADER_HEIGHT = HEADER_HEIGHT + 14;

interface Props {
  /** The increases the decision tree asks for, in percent, ascending. */
  recoIncreasePcts: number[];
  name: string;
  onNameChange: (name: string) => void;
  roundingDirection: RoundingDirection;
  onRoundingDirectionChange: (direction: RoundingDirection) => void;
  uhTypeFollow: UhTypeFollow;
  onUhTypeFollowChange: (follow: UhTypeFollow) => void;
  property: PropertyOption | null;
  onPropertyChange: (property: PropertyOption | null) => void;
  /** Set while the grid still holds what the history proposed. */
  proposal?: ProposedGrid | null;
  onClearProposal?: () => void;
  draft: GridDraft;
  onDraftChange: (draft: GridDraft) => void;
  disabled?: boolean;
}

// The two settings under the grid sit side by side and wrap together, so they
// share one width rather than each having its own.
const FIELD_WIDTH = 340;

const UH_TYPE_FOLLOW_OPTIONS: { value: UhTypeFollow; label: string }[] = [
  { value: "same_steps", label: "PRICING_TIER_FOLLOW_SAME_STEPS" },
  { value: "same_level", label: "PRICING_TIER_FOLLOW_SAME_LEVEL" },
  { value: "nothing", label: "PRICING_TIER_FOLLOW_NOTHING" },
];

/**
 * A step's colour, from the flattest of the grid to its widest.
 *
 * Read as a shape rather than as a verdict: the eye finds where a column
 * loosens without anyone having to fix a threshold nothing in the data
 * supports.
 */
const stepColour = (theme: Theme, ratio: number): string => {
  const low = decomposeColor(theme.palette.success.main).values;
  const high = decomposeColor(theme.palette.error.main).values;
  const at = Math.min(Math.max(ratio, 0), 1);
  const mix = (index: number) =>
    Math.round(low[index]! + (high[index]! - low[index]!) * at);
  return recomposeColor({
    type: "rgb",
    values: [mix(0), mix(1), mix(2)],
  });
};

/** A step as the grid prints it; an em dash where there is nothing to compare. */
const stepLabel = (
  formatter: FormatterService,
  step: number | null | undefined
): string =>
  step === null || step === undefined
    ? "\u2014"
    : formatter.format(step / 100, {
        style: "percent",
        signDisplay: "exceptZero",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

const GridEditor = ({
  recoIncreasePcts,
  name,
  onNameChange,
  roundingDirection,
  onRoundingDirectionChange,
  uhTypeFollow,
  onUhTypeFollowChange,
  property,
  onPropertyChange,
  proposal = null,
  onClearProposal,
  draft,
  onDraftChange,
  disabled,
}: Props) => {
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);
  const { classes } = formInputsStyles();
  const formatter = useRecoilValue(formatterService);
  const apiRef = useGridApiRef();
  // Every page of the tenant's properties: reloading them each time the dialog
  // opens costs one request per hundred properties for a list that never moves.
  const propertiesQuery = useQuery("pricing-tier-properties", fetchProperties, {
    staleTime: Infinity,
  });

  // Where a paste lands. Tracked from the last cell clicked rather than read
  // out of the grid's internals, so it stays predictable.
  const [anchor, setAnchor] = useState({ productIndex: 0, levelIndex: 0 });
  // Reading the shape of the ladder and typing prices into it are two
  // different jobs, so they take turns in the same cells rather than
  // crowding a second line under every input.
  const [showSteps, setShowSteps] = useState(false);
  const [fillingIndex, setFillingIndex] = useState<number | null>(null);
  const [propertyToSwitch, setPropertyToSwitch] =
    useState<PropertyOption | null>(null);
  const [rowMenu, setRowMenu] = useState<{
    element: HTMLElement;
    index: number;
  } | null>(null);

  const hasPrices = hasAnyPrice(draft);

  // ONE scale across the whole grid, so the same shade means the same price
  // whatever the row. Normalising each row against itself would paint the
  // identical gradient on every one of them — the columns already run from
  // cheap to dear, so that would say nothing the order does not.
  const priceRange = useMemo(() => {
    const prices = draft.products.flatMap((product) =>
      Object.values(product.prices)
        .map((price) => Number(price))
        .filter((price) => Number.isFinite(price) && price > 0)
    );
    if (prices.length === 0) return null;
    return { low: Math.min(...prices), high: Math.max(...prices) };
  }, [draft.products]);

  const generate = useMutation(fetchGeneratedLevels);

  const clearRow = (index: number) =>
    onDraftChange({
      ...draft,
      products: draft.products.map((product, position) =>
        position === index ? { ...product, prices: {} } : product
      ),
    });

  // Memoised because the column definitions below close over them, and a new
  // identity on every render would rebuild the whole grid on every keystroke.
  const setLevelLabel = useCallback(
    (key: string, label: string) =>
      onDraftChange({
        ...draft,
        levels: draft.levels.map((level) =>
          level.key === key ? { ...level, label } : level
        ),
      }),
    [draft, onDraftChange]
  );

  const removeLevel = useCallback(
    (key: string) =>
      onDraftChange({
        ...draft,
        levels: draft.levels.filter((level) => level.key !== key),
        products: draft.products.map((product) => {
          const { [key]: _removed, ...rest } = product.prices;
          return { ...product, prices: rest };
        }),
      }),
    [draft, onDraftChange]
  );

  const addLevel = useCallback(
    () => onDraftChange({ ...draft, levels: [...draft.levels, newLevel()] }),
    [draft, onDraftChange]
  );

  const steps = useMemo(() => stepsOf(draft), [draft]);
  // The widest step of the whole grid, so the gradient reads across columns
  // rather than restarting on each one.
  const widestStep = useMemo(
    () =>
      steps
        .flat()
        .reduce<number>(
          (top, step) => (step != null && step > top ? step : top),
          0
        ),
    [steps]
  );

  const columns: GridColDef[] = useMemo(() => {
    const productColumn: GridColDef = {
      field: "product",
      headerName: t("PRICING_TIER_PRODUCT"),
      width: PRODUCT_COLUMN_WIDTH,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography variant="body2" noWrap>
            {params.row.product}
          </Typography>
          {params.row.rule && (
            <Typography variant="caption" color="primary.main" noWrap>
              {params.row.rule}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton
            className="row-actions"
            aria-label={t("PRICING_TIER_ROW_ACTIONS")}
            size="small"
            disabled={disabled}
            onClick={(event) =>
              setRowMenu({
                element: event.currentTarget,
                index: Number(params.row.index),
              })
            }
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    };

    const levelColumns: GridColDef[] = draft.levels.map((level, index) => ({
      field: level.key,
      width: LEVEL_COLUMN_WIDTH,
      sortable: false,
      editable: !disabled,
      align: "right",
      headerAlign: "left",
      cellClassName: "level-cell",
      // One line: the rank stands in for the name until the analyst gives one
      // — Corsica Linea numbers its levels from "-1". Two lines and a separate
      // rank cost the width the prices need.
      renderHeader: () => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
          }}
        >
          <TextField
            variant="standard"
            value={level.label}
            placeholder={String(index + 1)}
            onChange={(event) => setLevelLabel(level.key, event.target.value)}
            disabled={disabled}
            slotProps={{
              htmlInput: {
                "aria-label": t("PRICING_TIER_LEVEL_NAME"),
                style: { fontSize: "0.75rem", fontWeight: 700 },
              },
            }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <IconButton
            className="level-remove"
            aria-label={t("PRICING_TIER_REMOVE_LEVEL")}
            onClick={() => removeLevel(level.key)}
            disabled={disabled || draft.levels.length <= 1}
            size="small"
            sx={{ p: 0 }}
          >
            <DeleteOutlineIcon sx={{ fontSize: "0.85rem" }} />
          </IconButton>
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const product = draft.products[Number(params.row.index)];
        if (!product) return null;

        const raw = String(params.value ?? "").trim();
        const error = cellError(product, draft.levels, index);
        const tint = priceTint(Number(raw), priceRange);
        const step = steps[Number(params.row.index)]?.[index] ?? null;

        return (
          <Tooltip title={error ? t(error) : ""}>
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.5,
                width: "100%",
                height: 26,
                px: 1,
                borderRadius: 1,
                border: 1,
                borderColor: error ? "error.main" : "divider",
                backgroundColor: "background.input",
                // Laid OVER the surface rather than replacing it, so a cheap
                // cell keeps the same ground as an empty one and only the
                // tint separates them.
                backgroundImage: showSteps ? "none" : tint(theme),
                color:
                  showSteps && step != null && widestStep > 0
                    ? stepColour(theme, step / widestStep)
                    : raw === ""
                    ? "text.secondary"
                    : "text.primary",
                fontVariantNumeric: "tabular-nums",
                "&:hover": { borderColor: "primary.main" },
              })}
            >
              {showSteps
                ? stepLabel(formatter, step)
                : raw === ""
                ? "—"
                : formatter.format(Number(raw), { maximumFractionDigits: 2 })}
            </Box>
          </Tooltip>
        );
      },
    }));

    // Where the next level will appear — the Excel / Airtable habit, instead of
    // a button a thousand pixels away from the last column.
    const addColumn: GridColDef = {
      field: "add",
      headerName: "",
      width: ADD_COLUMN_WIDTH,
      sortable: false,
      renderHeader: () => (
        <Tooltip title={t("PRICING_TIER_ADD_LEVEL")}>
          <span>
            <IconButton
              aria-label={t("PRICING_TIER_ADD_LEVEL")}
              size="small"
              onClick={addLevel}
              disabled={disabled}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
      renderCell: () => null,
    };

    return [productColumn, ...levelColumns, addColumn];
  }, [
    draft,
    disabled,
    t,
    formatter,
    priceRange,
    addLevel,
    removeLevel,
    setLevelLabel,
    steps,
    showSteps,
    widestStep,
  ]);

  const rows: GridRowModel[] = draft.products.map((product, index) => {
    return {
      id: product.uh_cat,
      index,
      product: product.short_name,
      uhType: product.uh_type_name,
      ...draft.levels.reduce<Record<string, string>>((cells, level) => {
        cells[level.key] = product.prices[level.key] ?? "";
        return cells;
      }, {}),
    };
  });

  const commitCell = (updated: GridRowModel) => {
    onDraftChange({
      ...draft,
      products: draft.products.map((product) =>
        product.uh_cat === updated.id
          ? {
              ...product,
              prices: draft.levels.reduce<Record<string, string>>(
                (cells, level) => {
                  const value = String(updated[level.key] ?? "").trim();
                  if (value !== "") cells[level.key] = value;
                  return cells;
                },
                {}
              ),
            }
          : product
      ),
    });
    return updated;
  };

  // Copied, not exported: the analyst pastes it into a spreadsheet, fills it
  // there and pastes it back into this same grid.
  const [copied, setCopied] = useState(false);
  const handleCopyGrid = async () => {
    await navigator.clipboard.writeText(gridToClipboard(draft));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const block = stripHeaders(
      parseClipboard(event.clipboardData.getData("text/plain"))
    );
    if (block.length === 0 || (block.length === 1 && block[0]?.length === 1)) {
      return; // a single value: let the cell editor handle it normally
    }
    event.preventDefault();
    // One click opens the anchor cell for editing, so an editor is holding its
    // old value when the paste lands. Closed first, discarding what it holds:
    // left open it commits on the way out and writes that value back over the
    // one just pasted — which is why the top-left cell came back empty.
    const anchorProduct = draft.products[anchor.productIndex];
    const anchorLevel = draft.levels[anchor.levelIndex];
    if (anchorProduct && anchorLevel) {
      const cell = { id: anchorProduct.uh_cat, field: anchorLevel.key };
      if (apiRef.current?.getCellMode(cell.id, cell.field) === "edit") {
        apiRef.current.stopCellEditMode({ ...cell, ignoreModifications: true });
      }
    }
    onDraftChange(pasteInto(draft, block, anchor));
  };

  const askPropertyChange = (next: PropertyOption | null) => {
    // Rows come from the hotel, so switching it empties the grid. Never in
    // silence once something has been typed.
    if (hasPrices && next?.id !== property?.id) {
      setPropertyToSwitch(next);
      return;
    }
    onPropertyChange(next);
  };

  const filling =
    fillingIndex === null ? null : draft.products[fillingIndex] ?? null;

  // A ladder taller than the cap scrolls, and its scrollbar takes width from
  // the columns — which is what put a thin horizontal scrollbar under a table
  // that otherwise fitted exactly.
  const contentHeight =
    GRID_HEADER_HEIGHT + draft.products.length * GRID_ROW_HEIGHT;
  const scrolls = contentHeight > GRID_MAX_HEIGHT;

  // The table sizes to its columns so the separators stop where the grid does,
  // instead of running across an empty half of the dialog.
  const tableWidth =
    PRODUCT_COLUMN_WIDTH +
    draft.levels.length * LEVEL_COLUMN_WIDTH +
    ADD_COLUMN_WIDTH +
    2 +
    (scrolls ? SCROLL_BAR_WIDTH : 0);
  // The dialog follows the table, not the other way round: the fields above
  // wrap inside that width instead of stretching the shell past the grid.
  // No ceiling of its own — `maxWidth: "100%"` below clamps it to whatever
  // the host dialog gives, and a ladder too long for that scrolls in its own
  // container. A fixed cap only guaranteed a scrollbar at ten levels.
  const editorWidth = Math.max(tableWidth, 560);
  // The shared grid Paper caps at 800px without clipping, so `autoHeight` on a
  // long ladder painted its rows straight over the fields below. The table gets
  // an explicit height instead: its content up to that cap, then it scrolls in
  // its own box and everything after it stays where the analyst left it.
  const tableHeight = Math.min(contentHeight, GRID_MAX_HEIGHT) + 2;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: editorWidth,
        maxWidth: "100%",
      }}
    >
      {/* The hotel leads, on its own: it is what the rows come from, and
          until one is picked there is no section to introduce. */}
      <Field label="PROPERTY" width={300}>
        <CustomAutocomplete
          sx={{ width: "100%", ...FIELD_INPUT }}
          size="small"
          options={propertiesQuery.data ?? []}
          loading={propertiesQuery.isLoading}
          value={property}
          getOptionLabel={(option: PropertyOption) =>
            option.hebergement_nom_court
          }
          isOptionEqualToValue={(
            option: PropertyOption,
            value: PropertyOption
          ) => option.id === value.id}
          onChange={(_event: React.SyntheticEvent, value: unknown) =>
            askPropertyChange((value as PropertyOption) ?? null)
          }
          disabled={disabled}
        />
      </Field>

      {property && (
        <Section
          title="PRICING_TIER_SECTION_GRID"
          // A hotel whose history holds no usable price still gets a proposal,
          // only an empty one. Reciting "0 nights, 0 below, 0 above" reads as
          // a broken computation rather than as an absence of data.
          subtitle={
            proposal
              ? proposalIsEmpty(proposal)
                ? "PRICING_TIER_PROPOSAL_EMPTY"
                : "PRICING_TIER_PROPOSAL_SOURCE"
              : undefined
          }
          subtitleValues={
            proposal && !proposalIsEmpty(proposal)
              ? {
                  nights: proposal.nights,
                  levels: proposal.levels,
                  from: proposal.date_from,
                  to: proposal.date_to,
                  below: proposal.nights_below,
                  above: proposal.nights_above,
                }
              : undefined
          }
        >
          <Box sx={{ maxWidth: "100%", overflowX: "auto", width: "100%" }}>
            {/* Above the grid they act on, not beside the hotel it came from.
                Copying is always offered — a hand-typed grid is worth taking to
                a spreadsheet too — while emptying only makes sense on a
                proposal. */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                width: tableWidth,
                maxWidth: "100%",
                mb: 0.5,
              }}
            >
              <AppButton
                intent={showSteps ? "primary" : "secondary"}
                onClick={() => setShowSteps(!showSteps)}
              >
                <FormattedMessage id="PRICING_TIER_SHOW_STEPS" />
              </AppButton>
              <AppButton intent="secondary" onClick={handleCopyGrid}>
                <FormattedMessage
                  id={copied ? "COPIED" : "PRICING_TIER_COPY_GRID"}
                />
              </AppButton>
              {proposal !== null && !proposalIsEmpty(proposal) && (
                <AppButton intent="secondary" onClick={onClearProposal}>
                  <FormattedMessage id="PRICING_TIER_PROPOSAL_CLEAR" />
                </AppButton>
              )}
            </Box>
            {/* On its own line under the buttons: a sentence long enough to
                explain the colours crowds them off the row it shares. */}
            {showSteps && recoIncreasePcts.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  width: tableWidth,
                  maxWidth: "100%",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  <FormattedMessage
                    id="PRICING_TIER_STEP_LEGEND"
                    values={{ percents: recoIncreasePcts.join(", ") }}
                  />
                </Typography>
              </Box>
            )}
            <Box
              onPaste={handlePaste}
              sx={{ width: tableWidth, maxWidth: "100%", height: tableHeight }}
            >
              <StyledDataGrid
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                hideFooter
                rowHeight={GRID_ROW_HEIGHT}
                columnHeaderHeight={GRID_HEADER_HEIGHT}
                // The category must stay readable while scrolling across levels.
                pinnedColumns={{ left: ["product"] }}
                processRowUpdate={commitCell}
                sx={{
                  // Same cell rule as the learning-models table: without it a
                  // two-line label sits high against a one-line neighbour.
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-cell.level-cell": {
                    justifyContent: "flex-end",
                  },
                  // The row handle and the level's delete only show on approach:
                  // three permanently visible destructive icons is noise.
                  "& .row-actions, & .level-remove": {
                    opacity: 0,
                    transition: "opacity 120ms",
                  },
                  "& .MuiDataGrid-row:hover .row-actions": { opacity: 1 },
                  "& .MuiDataGrid-columnHeader:hover .level-remove": {
                    opacity: 1,
                  },
                }}
                onCellClick={(params) => {
                  const levelIndex = draft.levels.findIndex(
                    (level) => level.key === params.field
                  );
                  if (levelIndex === -1) return;
                  setAnchor({
                    productIndex: Number(params.row.index),
                    levelIndex,
                  });
                  // A price grid is a spreadsheet: one click starts typing.
                  if (!disabled) {
                    apiRef.current?.startCellEditMode({
                      id: params.id,
                      field: params.field,
                    });
                  }
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-start",
                gap: 2,
                mt: 2,
              }}
            >
              {/* Not a tidy-up rule: this says which tier a price falling
            between two of them belongs to. */}
              <Field
                label="PRICING_TIER_SNAP"
                hint="PRICING_TIER_SNAP_HELP"
                width={FIELD_WIDTH}
              >
                <ExclusiveToggleGroup<RoundingDirection>
                  value={roundingDirection}
                  onChange={onRoundingDirectionChange}
                  size="medium"
                  options={SNAP_OPTIONS}
                />
              </Field>

              <Field
                label="PRICING_TIER_UH_TYPE_FOLLOW"
                hint="PRICING_TIER_UH_TYPE_FOLLOW_HELP"
                width={FIELD_WIDTH}
              >
                <ExclusiveToggleGroup<UhTypeFollow>
                  value={uhTypeFollow}
                  onChange={onUhTypeFollowChange}
                  size="medium"
                  options={UH_TYPE_FOLLOW_OPTIONS}
                />
              </Field>
            </Box>

            {/* Last of all: the ladder is named once it is what it will be. */}
            <Box sx={{ mt: 2 }}>
              <Field label="PRICING_TIER_GRID_NAME" width={420}>
                <TextField
                  size="small"
                  fullWidth
                  className={classes.inputText}
                  sx={FIELD_INPUT}
                  value={name}
                  onChange={(event) => onNameChange(event.target.value)}
                  disabled={disabled}
                />
              </Field>
            </Box>
          </Box>
        </Section>
      )}

      <Menu
        open={rowMenu !== null}
        anchorEl={rowMenu?.element}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          onClick={() => {
            setFillingIndex(rowMenu?.index ?? null);
            setRowMenu(null);
          }}
        >
          <FormattedMessage id="PRICING_TIER_FILL_ROW" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (rowMenu) clearRow(rowMenu.index);
            setRowMenu(null);
          }}
        >
          <FormattedMessage id="PRICING_TIER_CLEAR_ROW" />
        </MenuItem>
      </Menu>

      {filling && fillingIndex !== null && (
        <FillRuleDialog
          productName={filling.short_name}
          levels={draft.levels}
          onClose={() => setFillingIndex(null)}
          generating={generate.isLoading}
          onGenerate={(options) =>
            generate.mutate(
              { ...options, count: draft.levels.length },
              {
                onSuccess: ({ prices }) => {
                  onDraftChange({
                    ...draft,
                    products: draft.products.map((product, index) =>
                      index === fillingIndex
                        ? {
                            ...product,
                            prices: draft.levels.reduce<Record<string, string>>(
                              (cells, level, position) => {
                                const price = prices[position];
                                if (price !== undefined)
                                  cells[level.key] = price;
                                return cells;
                              },
                              {}
                            ),
                          }
                        : product
                    ),
                  });
                  setFillingIndex(null);
                },
              }
            )
          }
          onApply={(rule: FillRule) => {
            onDraftChange({
              ...draft,
              products: draft.products.map((product, index) =>
                index === fillingIndex
                  ? applyFillRule(product, draft.levels, rule)
                  : product
              ),
            });
            setFillingIndex(null);
          }}
        />
      )}

      <ResponsiveDialog
        fullWidth
        open={propertyToSwitch !== null}
        title={t("PROPERTY")}
        contentText={t("PRICING_TIER_SWITCH_PROPERTY_CONFIRM")}
        onClose={() => setPropertyToSwitch(null)}
        onSubmit={() => {
          onPropertyChange(propertyToSwitch);
          setPropertyToSwitch(null);
        }}
      />
    </Box>
  );
};

export default GridEditor;

/**
 * A flat wash of the accent, its strength read off where this price sits
 * between the grid's cheapest and dearest cell.
 *
 * Returned as a two-stop gradient rather than a background COLOUR so it
 * composites over the cell's own surface instead of replacing it; MUI's
 * `alpha` keeps the hue on the theme rather than on a literal.
 */
const priceTint =
  (price: number, range: { low: number; high: number } | null) =>
  (theme: Theme): string => {
    if (range === null || !Number.isFinite(price) || price <= 0) return "none";
    const span = range.high - range.low;
    const ratio = span <= 0 ? 1 : (price - range.low) / span;
    // The theme's foreground, not its accent: `primary.main` means "action"
    // and painted every priced cell the colour of a link. No token means
    // "magnitude", so the neutral is the one that collides with nothing.
    const wash = alpha(
      theme.palette.text.primary,
      TINT_MIN + ratio * (TINT_MAX - TINT_MIN)
    );
    return `linear-gradient(${wash}, ${wash})`;
  };
